import { Reveal, PerspectiveLines } from "@/components/Reveal";

const chapters = [
  {
    n: "01",
    title: "Find the idea at the centre",
    body: "Every organisation has a story. Our role is to find the idea at its centre — the truth that makes it worth listening to — before a single word of communication is written.",
  },
  {
    n: "02",
    title: "Give it clarity",
    body: "Complexity is the enemy of influence. We distil positioning, purpose and expertise into narratives with the clarity to cut through noise and hold attention.",
  },
  {
    n: "03",
    title: "Make it matter",
    body: "We build communications strategy that makes the story matter to the people who matter — stakeholders, media, institutions and the audiences that decide reputations.",
  },
  {
    n: "04",
    title: "Move beyond visibility",
    body: "The strongest communications are not simply seen — they are understood, remembered and acted upon. We create communication that moves beyond visibility towards genuine influence.",
  },
];

const pillars = ["Strategic thinking", "Editorial intelligence", "Creative expression", "Disciplined execution"];

export default function About() {
  return (
    <main data-testid="approach-page">
      <section className="mx-auto max-w-[1600px] px-6 pb-20 pt-40 sm:px-12 sm:pt-52" data-testid="approach-hero">
        <p className="mb-8 text-xs uppercase tracking-[0.3em] text-primary">Our Approach</p>
        <PerspectiveLines
          lines={["Understood.", "Remembered.", "Acted upon."]}
          className="font-syne text-[1.75rem] font-extrabold uppercase leading-[1.45] tracking-wider sm:text-[2.4rem] lg:text-[3.9rem]"
        />
        <Reveal delay={0.3}>
          <p className="font-editorial mt-10 max-w-2xl text-xl text-muted-foreground sm:text-2xl" data-testid="approach-hero-sub">
            We believe the strongest communications are not simply seen.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-12 sm:px-12" data-testid="manifesto-section">
        <Reveal>
          <p className="mb-16 text-xs uppercase tracking-[0.3em] text-muted-foreground">The Manifesto — Four Chapters</p>
        </Reveal>
        <div className="space-y-0">
          {chapters.map((c) => (
            <div key={c.n} className="grid grid-cols-1 gap-6 border-t border-black/10 py-16 sm:grid-cols-12 sm:gap-12 sm:py-24" data-testid={`manifesto-chapter-${c.n}`}>
              <div className="sm:col-span-4">
                <span className="sticky top-32 block font-syne text-7xl font-extrabold leading-none text-stroke sm:text-9xl">
                  {c.n}
                </span>
              </div>
              <div className="sm:col-span-8">
                <Reveal>
                  <h2 className="font-syne text-2xl font-extrabold uppercase tracking-wide sm:text-4xl">{c.title}</h2>
                  <p className="mt-6 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">{c.body}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-24 sm:px-12 sm:py-32" data-testid="approach-pillars-section">
        <Reveal>
          <h2 className="font-syne text-3xl font-extrabold uppercase tracking-wide sm:text-4xl lg:text-5xl">
            What we bring <span className="text-primary">together</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-px bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p, i) => (
            <Reveal key={p} delay={i * 0.08} className="bg-background">
              <div className="group flex h-full min-h-[200px] flex-col justify-between p-8 transition-colors duration-500 hover:bg-muted" data-testid={`approach-pillar-${i}`}>
                <span className="font-syne text-sm font-bold text-primary">0{i + 1}</span>
                <span className="font-syne text-xl font-bold uppercase tracking-wide transition-colors duration-300 group-hover:text-primary">{p}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="font-editorial mt-16 max-w-3xl text-2xl leading-snug text-foreground/80 sm:text-3xl">
            "Every organisation has a story. Our role is to find the idea at its centre, give it clarity and build a
            communications strategy that makes it matter to the people who matter."
          </p>
        </Reveal>
      </section>
    </main>
  );
}
